/**
 * Anonymous labels for mock tests (e.g. A1, B7). Stored in sessionStorage so
 * the same dialogue keeps the same code until the tab session ends.
 */

const STORAGE_KEY = 'naati-mock-dialogue-labels';

function allLetterDigitCodes(): string[] {
  const out: string[] = [];
  for (let i = 0; i < 26; i++) {
    const L = String.fromCharCode(65 + i);
    for (let d = 0; d <= 9; d++) {
      out.push(`${L}${d}`);
    }
  }
  return out;
}

function shuffleInPlace<T>(arr: T[]): void {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
}

function readStored(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, string>;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writeStored(map: Record<string, string>) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* ignore quota */
  }
}

/**
 * Ensures every dialogue id has a unique label; persists new assignments.
 */
export function ensureMockDialogueLabels(ids: number[]): Map<number, string> {
  const unique = Array.from(new Set(ids.filter((id) => Number.isFinite(id))));
  const stored = readStored();
  const used = new Set<string>(Object.values(stored));

  const pool = allLetterDigitCodes().filter((c) => !used.has(c));
  shuffleInPlace(pool);
  let poolIdx = 0;

  const out = new Map<number, string>();

  for (const id of unique) {
    const key = String(id);
    if (stored[key]) {
      out.set(id, stored[key]);
      continue;
    }
    let code: string;
    if (poolIdx < pool.length) {
      code = pool[poolIdx++];
    } else {
      code = `?${id}`;
    }
    stored[key] = code;
    used.add(code);
    out.set(id, code);
  }

  writeStored(stored);
  return out;
}

/** Single id convenience; returns a stable label for this session. */
export function getOrCreateMockDialogueLabel(dialogueId: number): string {
  const m = ensureMockDialogueLabels([dialogueId]);
  return m.get(dialogueId) ?? '?';
}
